<template>
  <a-empty
    v-if="initError"
    class="tph-error"
    :description="initError"
  />
  <div
    class="table-page-host"
    v-else-if="inited"
  >
    <div class="pri-wrapper">
      <transition name="left-slide">
        <div
          class="left-slide-outer"
          v-if="leftOpen && pageConfig?.isView && enable_view_panel"
        >
          <div class="left-wrapper">
            <leftLayout
              :pageConfig="pageConfig"
              v-model:open="leftOpen"
              :eventBus="bus"
              v-model:tableMode="tableMode"
            ></leftLayout>
          </div>
        </div>
      </transition>
      <div
        class="right-wrapper"
        ref="rightWrapperRef"
      >
        <div
          :class="['left-space', { 'setting-open': settingOpen }]"
          :style="{
            '--x': `${right_wrapper_left}px`,
            '--drawer_width': `${setting_drawer_width}px`,
          }"
        ></div>
        <rightTable
          :pageConfig="pageConfig"
          :modalType="modalType"
          ref="rightTableRef"
          :eventBus="bus"
          :app_flag="app_flag"
          v-model:leftOpen="leftOpen"
          :setting_drawer_width="setting_drawer_width"
          :tableMode="tableMode"
          :base_conditions_inject="inject_conditions"
          :query_params_inject="inject_params"
          :inherit_route_params="inherit_route_params"
          :title_inject="title_inject"
          :enable_ai_abilities="enable_ai_abilities"
          :enable_hash_sync="enable_hash_sync"
          :enable_btn_add="enable_btn_add"
          :view_key_namespace="view_key_namespace"
          :enable_advanced_search_multigroups="enable_advanced_search_multigroups"
          :search_display_type="search_display_type"
          v-model:heightlightRowKey="heightlightRowKey"
          @changeMode="onChangeMode"
        ></rightTable>
        <div
          :class="['right-space', { 'detail-open': detailState.open }]"
          :style="{
            '--detail_width': `${detailState.width}px`,
          }"
        ></div>
      </div>
    </div>
    <!-- display_type = 'bottom_grid' 的底部详情面板 -->
    <bottomGridDetail
      v-if="bottomState.open"
      :key="bottomState.key"
      :title="bottomState.title"
      :component="bottomState.component"
      :cmpObj="bottomState.cmpObj"
      :extra="bottomState.extra"
      :height="bottomState.height"
      :minHeight="bottomState.minHeight"
      :hideTitle="bottomState.hideTitle"
      :depth="depth + 1"
      @close="closeBottomDetail"
    ></bottomGridDetail>
  </div>
  <!-- display_type = 'right_drawer'（历史默认）的右侧抽屉详情 -->
  <a-drawer
    :title="detailState.title"
    :width="detailState.width"
    placement="right"
    v-model:open="detailState.open"
    :mask="false"
    :push="false"
    v-if="detailState.open"
  >
    <component
      :is="detailState.component"
      :cmpObj="detailState.cmpObj"
      :extra="detailState.extra"
      :depth="depth + 1"
    ></component>
  </a-drawer>
</template>
<script setup lang="ts">
import {
  onMounted,
  ref,
  reactive,
  provide,
  computed,
  onUnmounted,
  onActivated,
  onDeactivated,
  nextTick,
  watch,
} from 'vue';
import leftLayout from './leftLayout.vue';
import rightTable from './rightTable.vue';
import bottomGridDetail from './bottomGridDetail.vue';
import mitt from 'mitt';
import { useUserStore } from 'liyu-pc-base/init-plugin';
import { event_obj, useInjectMenuState } from 'liyu-pc-base/init-plugin';
import { getDefaultAndServerConfig, getPageConfig } from '../index.js';
import { useI18n } from 'vue-i18n';
import { message } from 'ant-design-vue';
import { PageConfig } from '../typing.js';

// 页面壳逻辑参数化：index.vue（路由级薄壳）与 detailTablePro（嵌套详情薄壳）共用。
// - show_detail 只走本实例总线 bus；全局 event_obj() 监听仅最外层（depth===0）作为兼容入口保留；
// - refresh / reset_and_reload / local_refresh 仅最外层听全局（嵌套层不被主页面刷新连带）；
// - depth 由上层 Host 的详情渲染传入（<component :is :depth="depth+1">），max_depth 统一在此守卫。
interface Props {
  app_flag: string;
  modalType: string;
  // ---- 过滤/参数注入（转发给 rightTable 的注入点）----
  inject_conditions?: any[]; // 追加到 advanced_base_conditions，强制走 conditions 通道
  inject_params?: Record<string, any>; // 追加 query_params
  inherit_route_params?: boolean; // 是否把当前路由 query 作为基础条件（详情表场景传 false）
  inject_form_defaults?: Record<string, any>; // 「新建」表单预置值（合并进 formModalConfig.model；详情表预置外键用）
  // ---- 行为开关（默认值 = 主页面现状）----
  enable_ai_abilities?: boolean;
  enable_hash_sync?: boolean; // 读写 window.location.hash（视图同步）
  enable_btn_add?: boolean;
  enable_view_panel?: boolean; // 左侧视图面板（详情表场景传 false）
  // ---- 身份/存储 ----
  view_key_namespace?: string;
  use_store?: boolean; // getPageConfig 是否写全局 store（详情表传 false，防污染）
  title_inject?: string; // 覆盖表格标题
  setting_drawer_width?: number; // 默认 1000（= 原 index.vue 主页面行为）；详情表场景由 detailTablePro 传 extra.setting_drawer_width（默认 800）
  enable_pagination?: boolean; // 覆盖 pageConfig.is_pagination；undefined = 不覆盖（跟随目标页面配置）
  enable_advanced_search_multigroups?: boolean; // 多组查询；undefined = 跟随目标页面（主页面现状），布尔 = 强制（详情表恒传，默认 false 单组）
  hide_header?: boolean; // 覆盖 pageConfig.hide_header；undefined = 不覆盖（跟随目标页面配置）
  search_display_type?: 'hide' | 'hide_and_show_summary' | 'show' | 'show_no_hide'; // 覆盖 tablePro 的 search_display_type；undefined = 不覆盖（rightTable 默认 'show'（主页面），详情表默认 'hide_and_show_summary'）
  // ---- 嵌套 ----
  depth?: number; // 当前嵌套层级：index.vue 为 0，详情表每层 +1
  listen_global_show_detail?: boolean; // 全局 show_detail 兼容监听（仅最外层开）
  max_depth?: number; // 最大嵌套层数（depth+1 > max_depth 时拦截并提示）
}
const props = withDefaults(defineProps<Props>(), {
  inject_conditions: () => [],
  inject_params: () => ({}),
  inherit_route_params: true,
  inject_form_defaults: () => ({}),
  enable_ai_abilities: true,
  enable_hash_sync: true,
  enable_btn_add: true,
  enable_view_panel: true,
  view_key_namespace: 'remotePagesV2',
  use_store: true,
  title_inject: '',
  setting_drawer_width: 1000, // 原 index.vue 主页面即为 1000，保持零变化
  enable_pagination: undefined,
  enable_advanced_search_multigroups: undefined,
  hide_header: undefined,
  search_display_type: undefined,
  depth: 0,
  listen_global_show_detail: true,
  max_depth: 2,
});

const emit = defineEmits(['changeMode', 'detail_open', 'detail_close']);

const leftOpen = ref(true);
const inited = ref(false);
const initError = ref('');
const rightTableRef = ref(null);
const bus = mitt();
const tableMode = ref<'normal' | 'pivot' | 'pivot_charts'>('normal');
const userStore = useUserStore();
const pageConfig = ref<PageConfig | null>(null);
const { t } = useI18n();
const current_org = userStore.currentOrg;
const setting_drawer_width = ref(props.setting_drawer_width);
const rightWrapperRef = ref(null);
const right_wrapper_left = ref(0);
const heightlightRowKey = ref(null);
const injectMenuState = useInjectMenuState();
const { collapsed } = injectMenuState();
const detailState = reactive({
  open: false,
  component: null,
  width: 0,
  title: '详情',
  cmpObj: null,
  extra: null,
  lastLeftOpen: true,
  lastMenuCollapsed: true,
});
// display_type = 'bottom_grid' 时的底部面板状态（与 detailState 互斥）
const bottomState = reactive({
  open: false,
  key: '',
  component: null,
  title: '详情',
  height: 300,
  minHeight: 100,
  hideTitle: false,
  cmpObj: null,
  extra: null,
});

const calcRightWrapperLeft = () => {
  if (rightWrapperRef.value) {
    const rect = (rightWrapperRef.value as HTMLElement).getBoundingClientRect();
    right_wrapper_left.value = rect.left;
  }
};

const tableProState = computed(() => {
  return rightTableRef.value?.activeTableRef;
});
const settingOpen = computed(() => {
  return tableProState.value?.settingOpen;
});

provide('tableProStateProxy', tableProState);

const init = async () => {
  try {
    const res = await getDefaultAndServerConfig({
      org_id: current_org.id,
      app_flag: props.app_flag,
      modalType: props.modalType,
    });
    pageConfig.value = getPageConfig(props.app_flag, props.modalType, res, t, {
      use_store: props.use_store,
      to_i18n: true,
      enable_btn: false,
    });
  } catch (e: any) {
    // 配置加载失败（如 modalType 写错）给出明确报错，而非停留在白屏
    initError.value = `页面配置加载失败（${props.app_flag}-${props.modalType}）：${
      e?.message || e
    }`;
    console.error(initError.value, e);
    inited.value = true;
    return;
  }
  if (!pageConfig.value?.isView) {
    leftOpen.value = false;
  } else if (pageConfig.value?.default_hide_view) {
    leftOpen.value = false;
  } else {
    leftOpen.value = true;
  }
  // 「新建」表单预置值：合并进 formModalConfig.model（form-modal 以 {...form.model} 初始化表单，
  // btnAdd 点击时才读取，此处提前合并即可生效）。主页面从不传该 prop，行为不变。
  if (
    pageConfig.value?.formModalConfig &&
    Object.keys(props.inject_form_defaults || {}).length > 0
  ) {
    pageConfig.value.formModalConfig.model = {
      ...(pageConfig.value.formModalConfig.model || {}),
      ...props.inject_form_defaults,
    };
  }

  // 分页覆盖：enable_pagination 传了布尔值时改写 pageConfig.is_pagination（rightTable 构建配置时读取）；
  // 未传（undefined）保持目标页面自身配置。主页面从不传该 prop，行为不变。
  if (props.enable_pagination !== undefined) {
    pageConfig.value.is_pagination = props.enable_pagination;
  }

  // 表头隐藏覆盖：hide_header 传了布尔值时改写 pageConfig.hide_header（rightTable 构建配置时读取）；
  // 未传（undefined）保持目标页面自身配置。
  if (props.hide_header !== undefined) {
    pageConfig.value.hide_header = props.hide_header;
  }

  // URL hash 决定初始视图模式（仅最外层页面开 hash 同步；详情表不读写 hash）
  const hash = props.enable_hash_sync ? window.location.hash.substring(1) : '';
  let tmpModel = undefined;
  if (hash) {
    if (hash.startsWith('pivotcharts:')) {
      tmpModel = 'pivot_charts';
    } else if (hash.startsWith('pivot:')) {
      tmpModel = 'pivot';
    } else {
      tmpModel = 'normal';
    }
  }
  if (!pageConfig.value?.enable_view_types?.length) {
    tmpModel = undefined;
  } else {
    tmpModel = tmpModel || pageConfig.value?.default_view_type || 'normal';
    const isFind = pageConfig.value?.enable_view_types?.includes(tmpModel);
    if (!isFind) {
      tmpModel = pageConfig.value?.enable_view_types[0];
    }
  }
  tableMode.value = tmpModel;
};

const event_obj_reinit = () => {
  init();
  tableProState.value?.init();
};
const event_obj_refresh = () => {
  tableProState.value?.refresh();
};
const closeBottomDetail = () => {
  bottomState.open = false;
  heightlightRowKey.value = null;
  emit('detail_close');
};

const handleShowDetail = ({ key, cmpObj }) => {
  const detailConfigItem = pageConfig?.value?.detailConfigs?.[key];
  if (!detailConfigItem) {
    console.warn(`detailConfigItem not found, key: ${key}`);
    return;
  }
  if (props.depth + 1 > props.max_depth) {
    heightlightRowKey.value = null;
    return;
  }
  const display_type = detailConfigItem.display_type || 'right_drawer';
  // extra：详情组件的私有配置（配置期常量），原样透传给详情组件
  const extra = detailConfigItem.extra ?? null;
  if (display_type === 'bottom_grid') {
    // 底部面板：占据页面下半部分且横向全宽，不抢横向空间，因此不收起左侧视图与菜单
    bottomState.key = key;
    bottomState.component = detailConfigItem.component;
    bottomState.title = detailConfigItem.title || '详情';
    bottomState.height = detailConfigItem.height || 300;
    bottomState.minHeight = detailConfigItem.min_height || 100;
    bottomState.hideTitle = !!detailConfigItem.hide_title;
    bottomState.cmpObj = cmpObj;
    bottomState.extra = extra;
    bottomState.open = true;
    // 与抽屉互斥
    detailState.open = false;
  } else {
    // right_drawer：右侧抽屉（历史行为）
    bottomState.open = false;
    detailState.component = detailConfigItem.component;
    detailState.width = detailConfigItem.width || 500;
    detailState.title = detailConfigItem.title || '详情';
    detailState.cmpObj = cmpObj;
    detailState.extra = extra;
    if (!detailState.open) {
      detailState.open = true;
      detailState.lastLeftOpen = leftOpen.value;
      detailState.lastMenuCollapsed = collapsed.value;
      leftOpen.value = false;
      collapsed.value = true;
    }
  }
  emit('detail_open', { key, display_type });
};

const onChangeMode = (mode: 'normal' | 'pivot' | 'pivot_charts') => {
  tableMode.value = mode;
  emit('changeMode', mode);
};

const event_obj_local_refresh = () => {
  bus.emit('local_refresh');
};

const registerGlobalEvents = () => {
  // refresh / reset_and_reload / local_refresh 保持全局（本方案不扩大改动面）；
  // 但仅最外层（depth===0）监听——嵌套详情表不被主页面的全局刷新连带。
  if (props.depth === 0) {
    event_obj()?.on('refresh', event_obj_refresh);
    event_obj()?.on('reset_and_reload', event_obj_reinit);
    event_obj()?.on('local_refresh', event_obj_local_refresh);
  }
  // show_detail 走本实例总线（嵌套隔离的关键）；全局监听仅最外层保留为兼容入口
  bus.on('show_detail', handleShowDetail);
  if (props.depth === 0 && props.listen_global_show_detail) {
    event_obj()?.on('show_detail', handleShowDetail);
  }
};
const unregisterGlobalEvents = () => {
  event_obj()?.off('refresh', event_obj_refresh);
  event_obj()?.off('reset_and_reload', event_obj_reinit);
  event_obj()?.off('local_refresh', event_obj_local_refresh);
  bus.off('show_detail', handleShowDetail);
  event_obj()?.off('show_detail', handleShowDetail);
};

onMounted(async () => {
  registerGlobalEvents();
  await init();
  inited.value = true;
  nextTick(() => {
    calcRightWrapperLeft();
  });
});
onUnmounted(() => {
  unregisterGlobalEvents();
});
// keep-alive：Host 位于 index.vue 的 keep-alive 子树内，activated/deactivated 会传播到后代组件
onActivated(() => {
  unregisterGlobalEvents();
  registerGlobalEvents();
});
onDeactivated(() => {
  unregisterGlobalEvents();
});
watch(
  () => detailState.open,
  (newVal, oldVal) => {
    if (newVal != oldVal && !newVal) {
      heightlightRowKey.value = null;
      leftOpen.value = detailState.lastLeftOpen;
      collapsed.value = detailState.lastMenuCollapsed;
      emit('detail_close');
    }
  },
);

defineExpose({
  refresh: event_obj_refresh,
  reinit: event_obj_reinit,
  tableProState,
  pageConfig,
});
</script>
<style scoped lang="less">
.tph-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 24px 0;
}
.table-page-host {
  height: 100%;
  padding-left: 4px;
  padding-right: 4px;
  padding-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}
.pri-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  overflow: hidden;
  gap: 4px;
}
.left-slide-outer {
  height: 100%;
  overflow: hidden;
  width: 220px;
  background-color: white;
}
.left-wrapper {
  height: 100%;
  overflow: hidden;
  width: 220px;
  background-color: white;
  padding-left: 4px;
  padding-right: 4px;
}
.left-slide-enter-active {
  transition: width 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}
.left-slide-leave-active {
  transition: width 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}
.left-slide-enter-from,
.left-slide-leave-to {
  width: 0;
  opacity: 0;
}
.left-slide-leave-from,
.left-slide-enter-to {
  width: 220px;
  opacity: 1;
}
.right-wrapper {
  display: flex;
  flex: 1;
  height: 100%;
  overflow: hidden;
  background-color: white;
  flex-direction: row;
  justify-content: space-between;
}
.left-space {
  width: 0;
  transition: width 0.2s ease;
  &.setting-open {
    width: calc(var(--drawer_width) - var(--x));
  }
}
.right-space {
  width: 0;
  transition: width 0.2s ease;
  &.detail-open {
    width: calc(var(--detail_width));
  }
}
</style>
